// Menu order everywhere (spec 2026-10-12 §4, §6.3).
//
// Orders: thisway-api stamps `sortorder` (1-based) on every order line and
// on every option row. When any row of a list lacks a positive one (an API
// from before the change, a write endpoint's 0) the list keeps today's
// behaviour: lines in API order; options by group order, option order,
// name, id.
//
// Names compare ordinally, like the API (never localeCompare).

const hasRank = (row) => Number.isInteger(row?.sortorder) && row.sortorder > 0;

const allRanked = (rows) => rows.length > 0 && rows.every(hasRank);

const byRank = (a, b) => a.sortorder - b.sortorder;

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const text = (value) => (value == null ? '' : String(value));

const compareText = (a, b) => {
  const x = text(a);
  const y = text(b);
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
};

const byGroupThenOption = (a, b) =>
  num(a?.optiongroupdisplayorder) - num(b?.optiongroupdisplayorder) ||
  num(a?.displayorder) - num(b?.displayorder) ||
  compareText(a?.name, b?.name) ||
  compareText(a?.id, b?.id);

export const inMenuOrder = (orderitems) => {
  if (!Array.isArray(orderitems)) return [];
  const lines = [...orderitems];
  return allRanked(lines) ? lines.sort(byRank) : lines;
};

export const optionsInMenuOrder = (options) => {
  if (!Array.isArray(options)) return [];
  return [...options].sort(allRanked(options) ? byRank : byGroupThenOption);
};

// Carts (spec §4.3): the basket exists before the API sees it, so the cart
// applies the same rule from the store's menu. Index of a
// Menu/GetFullMenuBySlug `menus` payload, plain JSON for redux-persist:
//   items[itemId]     = [menu displayorder, name, id,
//                        category displayorder, name, id,
//                        product displayorder, name, id,
//                        item displayorder, name, id]
//   options[optionId] = [group displayorder, option displayorder]
// The item level ties like the others, so an equal key means the same item.
// Ids are matched case-insensitively (both map keys and lookups are
// lowercased, as in the Flutter carts); the ids inside a key stay raw, and
// the tiebreak compares them ordinally. There is no group key: a basket
// option has no optiongroupid, so each option holds its group's order.
const idKey = (value) => (value == null ? '' : String(value).toLowerCase());

export const buildMenuIndex = (menus) => {
  const items = {};
  const options = {};
  (Array.isArray(menus) ? menus : []).forEach((menu) =>
    (menu?.categories || []).forEach((category) =>
      (category?.products || []).forEach((product) => {
        (product?.items || []).forEach((item) => {
          if (!item?.id) return;
          items[idKey(item.id)] = [
            num(menu.displayorder), text(menu.name), text(menu.id),
            num(category.displayorder), text(category.name), text(category.id),
            num(product.displayorder), text(product.name), text(product.id),
            num(item.displayorder), text(item.name), text(item.id),
          ];
        });
        (product?.optionGroups || []).forEach((group) =>
          (group?.options || []).forEach((option) => {
            if (!option?.id) return;
            options[idKey(option.id)] = [num(group.displayorder), num(option.displayorder)];
          }),
        );
      }),
    ),
  );
  return { items, options };
};

const compareKeys = (a, b) => {
  for (let i = 0; i < a.length; i += 1) {
    const d = typeof a[i] === 'number' ? a[i] - b[i] : compareText(a[i], b[i]);
    if (d !== 0) return d;
  }
  return 0;
};

// A display-only sorted copy of the basket. An equal key (the same item)
// keeps add order; a line the index cannot place (no index yet, a product
// that left the menu, a basket persisted before this change) goes last, in
// add order. The basket array and the order payload are never reordered.
export const basketInMenuOrder = (items, index) => {
  if (!Array.isArray(items)) return [];
  const keyOf = (line) => index?.items?.[idKey(line?.item?.id)] || null;
  return items
    .map((line, at) => ({ line, at, key: keyOf(line) }))
    .sort((a, b) => {
      if (a.key && b.key) return compareKeys(a.key, b.key) || a.at - b.at;
      if (a.key) return -1;
      if (b.key) return 1;
      return a.at - b.at;
    })
    .map(({ line }) => line);
};

// A basket line's options: the menu's group order, then option order (the
// option's own snapshot when the menu does not have it), then name, id and
// add order.
export const basketOptionsInMenuOrder = (options, index) => {
  if (!Array.isArray(options)) return [];
  const placeOf = (option) =>
    index?.options?.[idKey(option?.id)] || [
      num(option?.optiongroupdisplayorder),
      num(option?.displayorder),
    ];
  return options
    .map((option, at) => ({ option, at, place: placeOf(option) }))
    .sort(
      (a, b) =>
        a.place[0] - b.place[0] ||
        a.place[1] - b.place[1] ||
        compareText(a.option?.name, b.option?.name) ||
        compareText(a.option?.id, b.option?.id) ||
        a.at - b.at,
    )
    .map(({ option }) => option);
};
